import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Play, X } from 'lucide-react';
import { Card } from '@/components/ui/card';

export interface VideoGuide {
  title: string;
  description?: string;
  src: string;
  channel?: 'social' | 'search' | 'both';
}

export function VideoModal({ src, title, onClose }: { src: string; title: string; onClose: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80" onClick={onClose}>
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="max-w-[85vw] max-h-[85vh] flex flex-col items-center gap-3" onClick={e => e.stopPropagation()}>
        <video src={src} controls autoPlay className="max-w-full max-h-[80vh] rounded-lg" />
        <p className="text-sm text-white/70">{title}</p>
      </div>
    </div>,
    document.body,
  );
}

function VideoCard({ video, onClick }: { video: VideoGuide; onClick: () => void }) {
  return (
    <Card
      className="shadow-none border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer group overflow-hidden flex-1 min-h-0 max-h-44 flex flex-col"
      onClick={onClick}
    >
      <div className="relative flex-1 min-h-0">
        <video
          src={video.src}
          muted
          preload="metadata"
          className="w-full h-full object-cover"
          onLoadedData={(e) => { (e.target as HTMLVideoElement).currentTime = 0.5; }}
        />
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
          <div className="w-10 h-10 rounded-full bg-white/20 group-hover:bg-white/30 flex items-center justify-center transition-colors backdrop-blur-sm">
            <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
          </div>
        </div>
      </div>
      <div className="px-3 py-2 flex-shrink-0">
        <p className="text-xs font-medium text-slate-800 leading-snug">{video.title}</p>
      </div>
    </Card>
  );
}

export function VideoGuidePanel({ videos }: { videos: VideoGuide[] }) {
  const [activeVideo, setActiveVideo] = useState<VideoGuide | null>(null);

  if (videos.length === 0) return null;

  return (
    <>
      {activeVideo && (
        <VideoModal src={activeVideo.src} title={activeVideo.title} onClose={() => setActiveVideo(null)} />
      )}

      <div className="h-[calc(100vh-6rem)] flex flex-col">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3 flex-shrink-0">
          Video Guides
        </p>
        <div className="flex-1 min-h-0 flex flex-col gap-2">
          {videos.map((video) => (
            <VideoCard key={video.src} video={video} onClick={() => setActiveVideo(video)} />
          ))}
        </div>
      </div>
    </>
  );
}
